import { Fragment, type ReactNode } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useToggleState from "@entrilia/shared/hooks/useToggleState";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type {
  FieldIdToValueMap,
  ObjectClassDefinition,
  ObjectFieldValues,
  ObjectFieldValuesDetails,
  PageLayout,
  RelatedObjectsUpdateRequests,
} from "../../api/types/objectTypes";
import FieldValuesSection from "./FieldValuesSection";
import type { FieldValuesSectionProps } from "./useFieldValuesEditor";

interface Props {
  object: ObjectFieldValues;
  objectDefinition: ObjectClassDefinition;
  cardLayout: PageLayout;
  allowEditing: boolean;
  onEntityUpdate: (entity: ObjectFieldValuesDetails) => void;
  renderFieldValues?: (params: FieldValuesSectionProps) => ReactNode;
}

const updateObject = withErrorHandling(adminApi.updateObject);

const ObjectFieldsSection = ({
  object,
  objectDefinition,
  cardLayout,
  allowEditing,
  onEntityUpdate,
  renderFieldValues,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [renderKey, toggleRenderKey] = useToggleState(false);

  const saveFieldValues = async (fieldValues: FieldIdToValueMap, relatedObjects?: RelatedObjectsUpdateRequests) => {
    if (Object.keys(fieldValues).length === 0 && relatedObjects === undefined) {
      toggleRenderKey();
      return true;
    }

    const [resp, error] = await updateObject(objectDefinition.objectType, object.id, {
      fieldValuesUserInput: fieldValues,
      relatedObjects,
    });
    if (error) {
      sendNotificationError(error.message);
      logError(error, `[ObjectDetailsSection (${objectDefinition.objectType})] saveFieldValues`);
      return false;
    }

    sendNotification("Changes saved");
    onEntityUpdate(resp);
    toggleRenderKey();
    return true;
  };

  const params: FieldValuesSectionProps = {
    objectType: objectDefinition.objectType,
    objectId: object.id,
    businessCentralType: objectDefinition.businessCentralType,
    fields: objectDefinition.fields,
    cardLayout,
    fieldValues: object.fieldValues,
    fieldsConfidence: object.fieldsConfidence,
    fieldsCitations: object.fieldsCitations,
    lookupObjects: object.lookupObjects,
    allowEditing,
    saveFieldValues,
  };

  return (
    <Fragment key={renderKey.toString()}>
      {renderFieldValues ? renderFieldValues(params) : <FieldValuesSection {...params} />}
    </Fragment>
  );
};

export default ObjectFieldsSection;
